import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCourses } from '../hooks/useCourses';
import { useUsers } from '../hooks/useUsers';
import { formatCurrency } from '../utils/formatUtils';

import {
  AcademicCapIcon,
  ClockIcon,
  UserIcon,
  StarIcon,
  PlayIcon,
  BookOpenIcon,
  UsersIcon,
  CheckIcon,
  ArrowRightIcon,
  ShoppingCartIcon,
  HeartIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import AIChatButton from '../components/AIChatButton';
import UserProfileLink from '../components/UserProfileLink';

const CourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { courses, loading, error } = useCourses();
  const { fetchUsers } = useUsers();
  
  const [course, setCourse] = useState<any>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Check for referral code in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      // Store referral code in localStorage for signup
      localStorage.setItem('referralCode', refCode);
    }
  }, [location]);

  // Find course by ID
  useEffect(() => {
    if (courseId && courses.length > 0) {
      const foundCourse = courses.find(c => c.id === courseId);
      setCourse(foundCourse);
    }
  }, [courseId, courses]);

  // Fetch course author data when course changes
  useEffect(() => {
    if (course?.author?.id) {
      fetchUsers([course.author.id]);
    }
  }, [course?.author?.id, fetchUsers]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading course details...</p>
          </div>
        </div>
        {/* Footer removed to avoid duplication; global PublicFooter is used */}
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AcademicCapIcon className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Course Not Found</h1>
            <p className="text-gray-600 mb-6">The course you're looking for doesn't exist or has been removed.</p>
            <button
              onClick={() => navigate('/catalog')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse All Courses
            </button>
          </div>
        </div>
        {/* Footer removed to avoid duplication; global PublicFooter is used */}
      </div>
    );
  }

  const handleEnroll = () => {
    if (currentUser) {
      // User is logged in, redirect to dashboard
      navigate('/app');
    } else {
      // User is not logged in, redirect to signup with referral code
      const signupUrl = referralCode ? `/signup?ref=${referralCode}` : '/signup';
      navigate(signupUrl);
    }
  };

  const handleSignIn = () => {
    const signinUrl = referralCode ? `/signin?ref=${referralCode}` : '/signin';
    navigate(signinUrl);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Course Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Course Image */}
            <div className="relative h-64 lg:h-full bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
              {course.image_url ? (
                <img
                  src={course.image_url}
                  alt={course.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-4 opacity-60">📚</div>
                    <div className="text-gray-500 text-sm font-medium">No Image</div>
                  </div>
                </div>
              )}
              
              {/* Price Badge */}
              <div className="absolute top-4 left-4">
                <span className={`px-4 py-2 text-sm font-bold rounded-2xl shadow-lg backdrop-blur-sm ${
                  course.price_status === "Free"
                    ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                }`}>
                  {course.price_status === "Free" ? "Free" : formatCurrency(parseFloat(course.pricee))}
                </span>
              </div>

              {/* Program Badge */}
              {course.program && course.program.length > 0 && (
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-white bg-opacity-90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-800">
                    {course.program[0]}
                  </span>
                </div>
              )}
            </div>

            {/* Course Info */}
            <div className="p-8">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{course.name}</h1>
                <p className="text-gray-600 text-lg leading-relaxed">{course.summary}</p>
              </div>

              {/* Course Stats */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <ClockIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-semibold text-gray-900">{course.duration}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <UsersIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="text-sm text-gray-600">Students</p>
                  <p className="font-semibold text-gray-900">{course.students}</p>
                </div>
              </div>

              {/* Rating */}
              {course.rating > 0 && (
                <div className="flex items-center mb-6">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <StarIconSolid
                        key={i}
                        className={`h-5 w-5 ${i < Math.floor(course.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <span className="ml-2 text-gray-600">({course.rating})</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleEnroll}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center"
                >
                  <PlayIcon className="h-5 w-5 mr-2" />
                  {currentUser ? 'Go to Dashboard' : 'Enroll Now'}
                </button>
                
                {!currentUser && (
                  <button
                    onClick={handleSignIn}
                    className="w-full border-2 border-blue-600 text-blue-600 py-3 px-6 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-200"
                  >
                    I Already Have an Account
                  </button>
                )}
              </div>

              {/* Referral Code Notice */}
              {referralCode && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    <strong>Referral Code Applied:</strong> {referralCode}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    You'll get special benefits when you sign up!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Course Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">About This Course</h2>
              <p className="text-gray-700 leading-relaxed">
                {course.description || course.summary}
              </p>
            </div>

            {/* What You'll Learn */}
            {course.learnings && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4">What You'll Learn</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {course.learnings.split(',').map((learning: string, index: number) => (
                    <div key={index} className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{learning.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Requirements */}
            {course.requirements && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Requirements</h2>
                <div className="space-y-2">
                  {course.requirements.split(',').map((requirement: string, index: number) => (
                    <div key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{requirement.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Info Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Course Information</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-gray-700">
                    Instructor: {course.author ? (
                      <UserProfileLink 
                        userId={course.author.id}
                        fallbackName={course.author.name}
                        fallbackAvatarUrl={course.author.image_url}
                        className="hover:text-primary-600 transition-colors"
                      />
                    ) : (
                      'Unknown'
                    )}
                  </span>
                </div>
                <div className="flex items-center">
                  <BookOpenIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-gray-700">{course.lessons_count} lessons</span>
                </div>
                <div className="flex items-center">
                  <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-gray-700">Language: {course.language}</span>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white text-center">
              <h3 className="text-lg font-bold mb-2">Ready to Start Learning?</h3>
              <p className="text-blue-100 mb-4">Join thousands of students already enrolled</p>
              <button
                onClick={handleEnroll}
                className="w-full bg-white text-blue-600 py-2 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                {currentUser ? 'Continue Learning' : 'Get Started'}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer removed to avoid duplication; global PublicFooter is used */}
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default CourseDetail;
